import React, { useEffect, useRef } from 'react';
import {
  FaArrowRight, FaRoute, FaHandshake, FaSeedling, FaHandsHelping,
  FaUsers, FaBookOpen, FaCheck,
} from 'react-icons/fa';
import Footer from './Footer';
import './NewSteps.css';

const FORM_BASE = 'https://thecitychurchluzira.churchcenter.com/people/forms/';

const CLASSES = [
  { title: 'Truth', description: 'A discussion on God and salvation, and the foundation everything else is built on.', form: '1079987' },
  { title: 'Righteousness', description: 'What it means to live out your faith and stand right before God.', form: '1080060' },
  { title: 'The Dominion of Christ & the Believer’s Authority', description: 'Understanding the spiritual authority you carry as a believer.', form: '1080059' },
  { title: 'Communion with the Holy Spirit & Prayer', description: 'Developing a vibrant, daily prayer life and friendship with the Spirit.', form: '1080058' },
  { title: 'Bible Reading & Biblical Interpretation', description: 'How to read, understand and apply Scripture for yourself.', form: '1080057' },
  { title: 'Submission, Service & Accountability', description: 'Growing in Christian character within a community that knows you.', form: '1080056' },
  { title: 'The City Church', description: 'Our vision, mission and strategy, and where you fit in the story.', form: '1080061' },
  { title: 'Church Membership & Serving', description: 'What it looks like to belong to and build the church family.', form: '1080062' },
  { title: 'Questions & Answers', description: 'An open space to bring your questions and settle what matters most.', form: '1080063' },
];

const JOURNEY = [
  { icon: <FaRoute aria-hidden="true" />, title: 'Start', text: 'Say yes to Jesus and begin with the foundations of faith.' },
  { icon: <FaBookOpen aria-hidden="true" />, title: 'Learn', text: 'Work through nine short classes at your own pace.' },
  { icon: <FaHandshake aria-hidden="true" />, title: 'Connect', text: 'Meet the family and find your people in a Missional Community.' },
  { icon: <FaHandsHelping aria-hidden="true" />, title: 'Serve', text: 'Discover your gifts and put them to work on a team.' },
  { icon: <FaSeedling aria-hidden="true" />, title: 'Grow', text: 'Keep maturing into everything God made you to be.' },
];

const OUTCOMES = [
  'A clear understanding of the gospel and your new life in Christ',
  'Confidence to read and apply the Bible on your own',
  'A vibrant prayer life and daily walk with the Holy Spirit',
  'A place to belong within The City Church family',
];

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const els = root.querySelectorAll('.reveal');
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return ref;
}

const NewSteps = () => {
  const ref = useReveal();

  return (
    <div ref={ref}>
      {/* HERO */}
      <header className="ns-hero">
        <div className="tcc-container ns-hero__inner">
          <p className="tcc-eyebrow ns-hero__eyebrow">New here? Start here</p>
          <h1 className="ns-hero__title">Your journey starts here</h1>
          <p className="ns-hero__lead">
            The Next Steps classes lay the foundations of faith for the new believer and
            help you find your place in The City Church community. Nine short sessions,
            one clear path forward.
          </p>
          <div className="ns-hero__actions">
            <a className="tcc-btn tcc-btn--accent tcc-btn--lg" href="#ns-classes">
              Take your next step <FaArrowRight aria-hidden="true" />
            </a>
            <a className="tcc-btn tcc-btn--outline-on-dark tcc-btn--lg" href="#ns-journey">
              See the journey
            </a>
          </div>
          <ul className="ns-hero__stats">
            <li><strong>9</strong><span>Foundational classes</span></li>
            <li><strong>Any</strong><span>Pace that suits you</span></li>
            <li><strong>1</strong><span>Church family to belong to</span></li>
          </ul>
        </div>
      </header>

      {/* JOURNEY */}
      <section id="ns-journey" className="tcc-section ns-journey reveal" aria-labelledby="ns-journey-h" style={{ scrollMarginTop: '80px' }}>
        <div className="tcc-container">
          <div className="ns-head">
            <p className="tcc-eyebrow">The path</p>
            <h2 className="tcc-h2" id="ns-journey-h">Five steps, one family</h2>
            <p className="tcc-lead">Everyone&rsquo;s story is different, but the way forward is simple.</p>
          </div>
          <ol className="ns-timeline">
            {JOURNEY.map((s, i) => (
              <li className="ns-step" key={s.title}>
                <span className="ns-step__icon">{s.icon}</span>
                <span className="ns-step__num">Step {i + 1}</span>
                <h3 className="ns-step__title">{s.title}</h3>
                <p className="ns-step__text">{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CLASSES */}
      <section id="ns-classes" className="tcc-section tcc-section--surface2 ns-classes reveal" aria-labelledby="ns-classes-h" style={{ scrollMarginTop: '80px' }}>
        <div className="tcc-container">
          <div className="ns-head">
            <p className="tcc-eyebrow">The curriculum</p>
            <h2 className="tcc-h2" id="ns-classes-h">Spiritual growth curriculum</h2>
            <p className="tcc-lead">
              Nine classes that give new believers a foundational understanding of the
              faith and the tools to live it out. Tap any class to register.
            </p>
          </div>
          <ul className="ns-grid">
            {CLASSES.map((c, i) => (
              <li className="ns-card tcc-card tcc-card--hover" key={c.title}>
                <a className="ns-card__link" href={`${FORM_BASE}${c.form}`} target="_blank" rel="noopener noreferrer">
                  <span className="ns-card__badge">Class {i + 1}</span>
                  <h3 className="ns-card__title">{c.title}</h3>
                  <p className="ns-card__desc">{c.description}</p>
                  <span className="ns-card__cta">Register <FaArrowRight aria-hidden="true" /></span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="tcc-section ns-outcomes reveal" aria-labelledby="ns-out-h">
        <div className="tcc-container ns-outcomes__grid">
          <div className="ns-outcomes__intro">
            <p className="tcc-eyebrow">What you&rsquo;ll walk away with</p>
            <h2 className="tcc-h2" id="ns-out-h">Built to equip you for the long run</h2>
            <p className="ns-outcomes__lead">
              These classes are not about information for its own sake. They are about
              helping you live victoriously and grow into a confident, rooted disciple.
            </p>
          </div>
          <ul className="ns-outcomes__list">
            {OUTCOMES.map((o) => (
              <li className="ns-outcome" key={o}>
                <span className="ns-outcome__check"><FaCheck aria-hidden="true" /></span>
                <span>{o}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="ns-cta reveal" aria-labelledby="ns-cta-h">
        <div className="tcc-container ns-cta__inner">
          <span className="ns-cta__icon"><FaUsers aria-hidden="true" /></span>
          <h2 className="ns-cta__title" id="ns-cta-h">Ready to begin?</h2>
          <p className="ns-cta__text">
            Start with Class 1 today, or reach out and we will walk the whole journey
            with you. You are always welcome here.
          </p>
          <div className="ns-cta__actions">
            <a className="tcc-btn tcc-btn--on-dark tcc-btn--lg" href={`${FORM_BASE}${CLASSES[0].form}`} target="_blank" rel="noopener noreferrer">
              Start with Class 1 <FaArrowRight aria-hidden="true" />
            </a>
            <a className="tcc-btn tcc-btn--outline-on-dark tcc-btn--lg" href="/contact-us">
              Talk to our team
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NewSteps;
